import { Control, UseFormHandleSubmit } from "react-hook-form";
import { ActiveMarkerType, IDispatchInput } from "models/dispatch";
import { IFleetModel } from "models/fleetModel";
import { DefaultOptionType } from "antd/es/select";
import LabeledInput from "components/Form/LabeledInput";
import LabeledSelectInput from "components/Form/LabeledSelectInput";
import LabeledAutoComplete from "components/Form/LabeledAutoComplete";
import { Button, Spin } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import pickup from "assets/images/pickup.png";
import dropOff from "assets/images/drop-off.png";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IDispatchInput, undefined>;
  onSubmit: (data: IDispatchInput) => Promise<void>;
  control: Control<IDispatchInput, any>;
  data: IFleetModel[];
  loading: boolean;
  pickupSuggestion: DefaultOptionType[] | undefined;
  pickupSuggestionLoading: boolean;
  dropOffSuggestion: DefaultOptionType[] | undefined;
  dropOffSuggestionLoading: boolean;
  watchDriverId: string | null | undefined;
  requestLoading: { loading: boolean; message?: string | undefined };
  activeMarkerType?: ActiveMarkerType;
  tripEstimation:
  | {
    distance: string;
    duration: string;
    price: number | null;
    error: string | null;
  }
  | undefined;
  handleClearDriver: () => void;
  onPickupSelect: (_: string, { title }: { title: string }) => Promise<void>;
  onDropOffSelect: (_: string, { title }: { title: string }) => Promise<void>;
  onPickupSearch?: (value: string) => void;
  onDropOffSearch?: (value: string) => void;
  handleTogglePickupLocation: () => void;
  handleToggleDestinationLocation: () => void;
  handleCancelTrip: () => void;
  handleClearForm: () => void;
}

export default function DispatchRequest({
  control,
  data,
  dropOffSuggestion,
  dropOffSuggestionLoading,
  loading,
  watchDriverId,
  pickupSuggestion,
  pickupSuggestionLoading,
  requestLoading,
  activeMarkerType,
  tripEstimation,
  handleClearDriver,
  handleSubmit,
  handleToggleDestinationLocation,
  handleTogglePickupLocation,
  onDropOffSelect,
  onPickupSelect,
  onPickupSearch,
  onDropOffSearch,
  onSubmit,
  handleCancelTrip,
  handleClearForm,
}: IProps) {
  const handleDispatchClick = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🖱️ Dispatch button clicked - submitting directly");
    // Directly submit - the overlay will block all interaction until response
    handleSubmit(onSubmit)();
  };

  return (
    <div className="flex-1 bg-white shadow-lg flex relative overflow-hidden">
      <div className="flex-1 bg-white p-4 flex flex-col items-center overflow-y-auto">
        <div className="w-full text-left mt-2 mb-5">
          <p className="mb-5 text-xl">
            <strong>Book A Ride</strong>
          </p>
          <hr className="border-black w-full m-2" />
        </div>
        <form onSubmit={handleDispatchClick}>
          <div className="w-full flex flex-col items-center gap-y-4">
            <LabeledInput
              type="text"
              control={control}
              name="passenger_first_name"
              placeholder="Enter passenger first name"
              label="Passenger First Name"
              disabled={requestLoading.loading}
            />
            <LabeledInput
              type="text"
              control={control}
              name="passenger_last_name"
              placeholder="Enter passenger last name"
              label="Passenger Last Name"
              disabled={requestLoading.loading}
            />

            <LabeledInput
              type="tel"
              prefix={
                <span className="text-[rgba(0,0,0,0.88)] font-medium text-sm">
                  +251
                </span>
              }
              control={control}
              name="passenger_phone"
              placeholder="Enter passenger phone number"
              label="Passenger Phone Number"
              disabled={requestLoading.loading}
            />
            <LabeledSelectInput
              control={control}
              name="fleet_model_id"
              placeholder="Enter fleet model"
              label="Fleet Model"
              options={data.map((value) => ({
                label: value.name,
                value: value.id,
              }))}
              loading={loading}
              disabled={requestLoading.loading}
            />
            <LabeledAutoComplete
              control={control}
              name="pickup_location"
              placeholder="Enter pickup location"
              label="Pickup Location"
              options={pickupSuggestion}
              loading={pickupSuggestionLoading}
              isActive={activeMarkerType === "pickup"}
              onSelect={onPickupSelect}
              onSearch={onPickupSearch}
              handleToggleLocation={handleTogglePickupLocation}
              icon={pickup}
              disabled={requestLoading.loading}
            />
            <LabeledAutoComplete
              control={control}
              name="drop_off_location"
              placeholder="Enter drop off location"
              label="Drop Off Location"
              isActive={activeMarkerType === "destination"}
              options={dropOffSuggestion}
              loading={dropOffSuggestionLoading}
              onSelect={onDropOffSelect}
              onSearch={onDropOffSearch}
              handleToggleLocation={handleToggleDestinationLocation}
              icon={dropOff}
              disabled={requestLoading.loading}
            />
            {watchDriverId && (
              <div className="w-full border-l-4 border-gray-700 px-1 bg-gray-200 py-2 flex justify-between items-center rounded-r-md">
                <p>Driver selected </p>
                <Button
                  type="text"
                  shape="circle"
                  className="p-0"
                  size="small"
                  onClick={handleClearDriver}
                  disabled={requestLoading.loading}
                  icon={
                    <CloseCircleFilled
                      className="text-red-500"
                      style={{ fontSize: "18px" }}
                    />
                  }
                />
              </div>
            )}

            {tripEstimation &&
              (tripEstimation.error === null ? (
                <div className="w-full border-l-4 border-gray-700 px-2 bg-gray-200 py-0.5 gap-y-1 flex flex-col justify-between items-start rounded-r-md">
                  <p>
                    <b>Distance</b>: {tripEstimation.distance}
                  </p>
                  <p>
                    <b>Duration</b>: {tripEstimation.duration}
                  </p>
                  <p>
                    <b>Price</b>:{" "}
                    {tripEstimation.price &&
                      tripEstimation.price.toLocaleString("en-US")}{" "}
                    {tripEstimation.price ? " ETB" : "-"}
                  </p>
                </div>
              ) : (
                <div className="w-full border-l-4 text-sm py-2 border-gray-700 px-2 bg-gray-200 gap-y-1 flex flex-col justify-between items-start rounded-r-md">
                  <p className="text-red-500">{tripEstimation?.error}</p>
                </div>
              ))}
          </div>

          <div className="w-full flex items-center gap-6 mb-4 mt-4">
            <div className="flex-1 flex justify-between mt-3">
              <Button
                onClick={() => {
                  handleClearForm();
                }}
                type="primary"
                danger
                disabled={requestLoading.loading}
                className="py-2 px-12 bg-black text-white rounded h-auto"
              >
                Clear
              </Button>
              <Button
                loading={requestLoading.loading}
                type="primary"
                htmlType="submit"
                disabled={requestLoading.loading}
                className="py-2 px-8 bg-black text-white rounded h-auto"
              >
                Dispatch
              </Button>
            </div>
          </div>
        </form>
      </div>
      {requestLoading.loading && (
        <div className="flex flex-col grow gap-y-8 bg-[rgba(0,0,0,0.75)] absolute top-0 right-0 left-0 bottom-0 items-center justify-center z-50 pointer-events-auto">
          <Spin size="large" />
          <p className="text-white text-xl font-semibold">
            {requestLoading.message ?? "Searching for available drivers..."}
          </p>
          <p className="text-white text-sm mt-2">
            Please wait while we find a driver for this trip
          </p>
          <Button
            type="primary"
            danger
            onClick={handleCancelTrip}
            className="py-2 px-12 text-white rounded h-auto mt-4"
          >
            Cancel Request
          </Button>
        </div>
      )}
    </div>
  );
}
